import L from 'leaflet';
import { Drone } from '../types/drone';
import { DEFAULT_MAP_ZOOM } from '../hooks/useInitialMapView';

/**
 * Where the fleet is relative to what the operator can actually see (#589).
 *
 * Until this existed, "not shown on the map" meant one thing: no coordinates.
 * A drone with a perfectly good position 120 km outside the viewport was just
 * as absent from the dashboard and was counted nowhere, so the arithmetic an
 * operator was given did not close — five drones, zero markers, an explanation
 * covering three.
 *
 * The three buckets are exhaustive and disjoint by construction, which is the
 * property worth having: `inView + offScreen + unplaceable` is the fleet, so a
 * screen that reports the last two cannot leave an aircraft unaccounted for.
 */
export interface FleetPlacement {
  /** Placeable and inside the current viewport — the markers being drawn. */
  inView: Drone[];
  /** Placeable, drawn, and outside the viewport. Invisible, and previously uncounted. */
  offScreen: Drone[];
  /** No reported position at all, so nothing to draw (#508). */
  unplaceable: Drone[];
}

/**
 * The drone's coordinates, or null when it has never reported a position.
 *
 * `0,0` counts as a position here. It is almost certainly a never-measured
 * fix rather than a drone in the Gulf of Guinea — that is #577's argument and
 * #577's call to make. Treating it as placeable keeps this change to one
 * question, and has the useful side effect of drawing the absurdity on the
 * map instead of hiding it in a counter.
 */
export const droneLatLng = (drone: Drone): L.LatLng | null =>
  drone.position
    ? L.latLng(drone.position.latitude, drone.position.longitude)
    : null;

/**
 * Split a fleet by what the viewport shows.
 *
 * `bounds` of null means the viewport is not knowable yet — a map that has not
 * been laid out reports a zero-sized box, and every drone would look off-screen.
 * Claiming that would be worse than saying nothing, so a placeable drone counts
 * as in view until there is a real viewport to judge it against.
 */
export const placeFleet = (
  drones: Drone[],
  bounds: L.LatLngBounds | null
): FleetPlacement => {
  const placement: FleetPlacement = { inView: [], offScreen: [], unplaceable: [] };
  for (const drone of drones) {
    const at = droneLatLng(drone);
    if (at === null) {
      placement.unplaceable.push(drone);
    } else if (bounds === null || bounds.contains(at)) {
      placement.inView.push(drone);
    } else {
      placement.offScreen.push(drone);
    }
  }
  return placement;
};

/** The smallest box holding every placeable drone, or null if none can be placed. */
export const fleetBounds = (drones: Drone[]): L.LatLngBounds | null => {
  const points = drones
    .map(droneLatLng)
    .filter((at): at is L.LatLng => at !== null);
  return points.length > 0 ? L.latLngBounds(points) : null;
};

/**
 * How to frame the fleet when fitting to it.
 *
 * `maxZoom` matters: a one-drone fleet is a zero-sized box, and `fitBounds`
 * answers that with zoom 19 — a rooftop, with no context around it. Capping at
 * the zoom a map opens on keeps "show me the fleet" meaning a fleet view.
 */
export const FLEET_FIT_OPTIONS: L.FitBoundsOptions = {
  padding: [48, 48],
  maxZoom: DEFAULT_MAP_ZOOM,
};
